import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Columns, AlignLeft, Loader2, Sparkles, AlertTriangle, Filter } from 'lucide-react';
import AnnotationTooltip from './AnnotationTooltip';
import HandwritingPlace from './HandwritingPlace';
import { buildAnnotatedSegments } from '../utils/highlightAnnotations';
import { confidencePercent } from '../utils/confidence';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { canCompareWith } from '../constants/features';
import './DiffViewer.css';

// Confidence threshold below which we visually flag a segment for review.
// Matches the banner threshold in ChatInterface (`lowConfidenceCount`).
const LOW_CONFIDENCE_THRESHOLD = 0.85;

const PULSE_DURATION_MS = 1600;

// The handwriting card opens above its value when this much room is left above it, below otherwise.
const HANDWRITING_CARD_ROOM_PX = 380;
const HANDWRITING_CARD_MAX_WIDTH_PX = 420;
const VIEWPORT_GUTTER_PX = 16;

// Renders one diff line with annotation highlights. `side` controls whether we
// match against annotation.source_text (left panel) or annotation.text (right
// panel). The wrapping spans carry data-* attributes so the parent can locate
// matching annotations across sides on click.
function AnnotatedLine({ line, annotations, side, marks, onAnnotationHover, onAnnotationLeave, onAnnotationClick,
  onMarkShow, onMarkLeave, lineRef }) {
  const segments = buildAnnotatedSegments(line, annotations, side, marks);
  return (
    <span className="line-content" ref={lineRef}>
      {segments.map((seg, i) => {
        if (seg.mark) {
          return (
            <span
              key={i}
              className="handwritten-value"
              tabIndex={0}
              data-testid="handwritten-value"
              onMouseEnter={(e) => onMarkShow(e, seg.mark)}
              onMouseLeave={onMarkLeave}
              onClick={(e) => onMarkShow(e, seg.mark)}
              onFocus={(e) => onMarkShow(e, seg.mark)}
              onBlur={onMarkLeave}
            >
              {seg.text}
            </span>
          );
        }
        if (!seg.match) return <React.Fragment key={i}>{seg.text}</React.Fragment>;
        return (
          <span
            key={i}
            className={`annotation type-${seg.annotation.type}`}
            data-annotation-idx={seg.idx}
            data-side={side}
            onMouseEnter={(e) => onAnnotationHover(e, seg.annotation)}
            onMouseLeave={onAnnotationLeave}
            onClick={(e) => onAnnotationClick(e, seg.idx, side)}
          >
            {seg.text}
          </span>
        );
      })}
    </span>
  );
}

function ConfidenceBadge({ confidence }) {
  if (confidence === undefined || confidence === null) return null;
  let level = 'high';
  if (confidence < 0.65) level = 'low';
  else if (confidence < LOW_CONFIDENCE_THRESHOLD) level = 'medium';
  const pct = confidencePercent(confidence);
  const title = level === 'high'
    ? `Confidence: ${pct}%`
    : `Confidence: ${pct}% - review this segment`;
  return (
    <span
      className={`line-confidence-badge level-${level}`}
      title={title}
    >
      {level === 'high' ? '✓' : <AlertTriangle size={13} strokeWidth={2.5} />}
    </span>
  );
}

function isLowConfidence(confidence) {
  return typeof confidence === 'number' && confidence < LOW_CONFIDENCE_THRESHOLD;
}

function DiffViewer({ originalLines, translatedLines, annotations = [], sourceLang, targetLang, lineConfidence = {}, documentId,
  handwriting = [] }) {
  const { t } = useTranslation('components');
  const { user } = useAuth();
  const deeplComparison = canCompareWith(user, 'deepl');
  const [viewMode, setViewMode] = useState('side-by-side'); // 'side-by-side' or 'inline'
  const [activeAnnotation, setActiveAnnotation] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  // Filter: when on, hide segments with high confidence so the user can
  // focus on the ones below the threshold.
  const [showOnlyLowConf, setShowOnlyLowConf] = useState(false);
  const hideTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const [activeMark, setActiveMark] = useState(null);
  const markHideRef = useRef(null);

  // The copy's handwritten values by the line they stand on, each keeping its index for its scan place.
  const marksByLine = useMemo(() => {
    const byLine = {};
    handwriting.forEach((value, index) => {
      (byLine[value.line_index] = byLine[value.line_index] || []).push({ ...value, index });
    });
    return byLine;
  }, [handwriting]);

  const lowConfIndices = useMemo(() => {
    const out = [];
    for (const [k, v] of Object.entries(lineConfidence || {})) {
      if (isLowConfidence(v)) out.push(parseInt(k, 10));
    }
    return out.sort((a, b) => a - b);
  }, [lineConfidence]);

  const hasAnyLowConf = lowConfIndices.length > 0;

  // DeepL comparison state. The panel is hidden by default (small screens) and
  // animates in once the translator opts in. We keep the data once fetched so
  // toggling off/on is instant.
  const [deeplStatus, setDeeplStatus] = useState('idle'); // idle | loading | available | unavailable
  const [deeplLines, setDeeplLines] = useState(null);
  const [deeplReason, setDeeplReason] = useState(null);
  const [deeplOpen, setDeeplOpen] = useState(false);

  const maxLines = Math.max(
    originalLines.length,
    translatedLines.length,
    deeplLines ? deeplLines.length : 0,
  );
  const allLines = Array.from({ length: maxLines }, (_, i) => ({
    originalIndex: i,
    original: originalLines[i] || '',
    translated: translatedLines[i] || '',
    deepl: deeplLines ? (deeplLines[i] || '') : '',
  }));
  // When the low-conf filter is on, retain ONLY rows below the threshold.
  // Each entry keeps its originalIndex so line numbers stay aligned with
  // the source document instead of getting renumbered to 1..N.
  const lines = showOnlyLowConf
    ? allLines.filter((l) => isLowConfidence(lineConfidence[String(l.originalIndex)]))
    : allLines;

  // Auto-scroll to the first low-confidence segment shortly after mount.
  // Lets users jump straight to the segment flagged by the chat banner
  // without scrolling the whole diff manually.
  useEffect(() => {
    if (!hasAnyLowConf) return;
    const node = containerRef.current;
    if (!node) return;
    const t1 = setTimeout(() => {
      const first = node.querySelector('.diff-line.low-confidence');
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => clearTimeout(t1);
    // We deliberately don't depend on viewMode/showOnlyLowConf - only
    // auto-scroll on first mount; subsequent re-renders shouldn't yank
    // the user's scroll position around.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleDeepl = useCallback(async () => {
    // If we already loaded once, just open/close - don't re-fetch.
    if (deeplStatus === 'available' || deeplStatus === 'unavailable') {
      setDeeplOpen((open) => !open);
      return;
    }
    if (!documentId) return;

    setDeeplStatus('loading');
    setDeeplOpen(true);
    try {
      const data = await api.getComparison(documentId, 'deepl');
      if (data.available) {
        setDeeplLines(data.translated_lines || []);
        setDeeplStatus('available');
      } else {
        setDeeplReason(data.reason || 'api_error');
        setDeeplStatus('unavailable');
      }
    } catch (e) {
      console.error('DeepL comparison fetch failed:', e);
      setDeeplReason('api_error');
      setDeeplStatus('unavailable');
    }
  }, [deeplStatus, documentId]);

  const handleAnnotationHover = useCallback((e, annotation) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top > 150 ? rect.top : rect.bottom + 8;
    setActiveAnnotation(annotation);
    setTooltipPosition({ x, y });
  }, []);

  const handleMarkShow = useCallback((e, mark) => {
    if (markHideRef.current) {
      clearTimeout(markHideRef.current);
      markHideRef.current = null;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const half = Math.min(HANDWRITING_CARD_MAX_WIDTH_PX, window.innerWidth - 2 * VIEWPORT_GUTTER_PX) / 2;
    const x = Math.min(Math.max(rect.left + rect.width / 2, VIEWPORT_GUTTER_PX + half),
      window.innerWidth - VIEWPORT_GUTTER_PX - half);
    const above = rect.top > HANDWRITING_CARD_ROOM_PX;
    setActiveMark({ mark, position: { x, y: above ? rect.top : rect.bottom + 8, above } });
  }, []);

  const handleMarkLeave = useCallback(() => {
    markHideRef.current = setTimeout(() => setActiveMark(null), 200);
  }, []);

  const handleMarkCardEnter = useCallback(() => {
    if (markHideRef.current) {
      clearTimeout(markHideRef.current);
      markHideRef.current = null;
    }
  }, []);

  const handleAnnotationLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setActiveAnnotation(null);
      setTooltipPosition(null);
    }, 200);
  }, []);

  const handleTooltipMouseEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleTooltipMouseLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setActiveAnnotation(null);
      setTooltipPosition(null);
    }, 100);
  }, []);

  // Click on a highlighted span on one side → find the matching one on the
  // other side, scroll it into view, and pulse it briefly. Lets the translator
  // verify "this English term came from THAT Ukrainian word" in one click.
  const handleAnnotationClick = useCallback((e, idx, fromSide) => {
    if (!containerRef.current) return;
    const otherSide = fromSide === 'source' ? 'target' : 'source';
    const target = containerRef.current.querySelector(
      `.annotation[data-annotation-idx="${idx}"][data-side="${otherSide}"]`
    );
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.remove('annotation-pulse');
    // Force reflow so the animation can re-trigger if the user clicks rapidly.
    void target.offsetWidth;
    target.classList.add('annotation-pulse');
    setTimeout(() => {
      target.classList.remove('annotation-pulse');
    }, PULSE_DURATION_MS);
  }, []);

  return (
    <div className="diff-viewer" ref={containerRef}>
      <div className="diff-header">
        <h3>{t('diff.title')}</h3>
        <div className="diff-header-actions">
          {hasAnyLowConf && (
            <button
              className={`btn btn-ghost diff-lowconf-filter ${showOnlyLowConf ? 'active' : ''}`}
              onClick={() => setShowOnlyLowConf((v) => !v)}
              title={t('diff.lowConfFilterTooltip', 'Show only segments below 85% confidence')}
            >
              <Filter size={14} />
              <span>
                {t('diff.lowConfFilter', { count: lowConfIndices.length, defaultValue: 'Below 85% ({{count}})' })}
              </span>
            </button>
          )}
          {deeplComparison && (
            <button
              className={`btn btn-ghost diff-deepl-toggle ${deeplOpen ? 'active' : ''}`}
              onClick={handleToggleDeepl}
              disabled={deeplStatus === 'loading'}
              title={t('diff.compareWithDeepl', 'Compare with DeepL')}
            >
              {deeplStatus === 'loading' ? (
                <Loader2 size={16} className="diff-deepl-spinner" />
              ) : (
                <Sparkles size={16} />
              )}
              <span>{t('diff.deepl', 'DeepL')}</span>
            </button>
          )}
          <div className="view-toggle">
            <button
              className={`btn btn-ghost ${viewMode === 'side-by-side' ? 'active' : ''}`}
              onClick={() => setViewMode('side-by-side')}
              title={t('diff.sideBySide')}
            >
              <Columns size={18} />
            </button>
            <button
              className={`btn btn-ghost ${viewMode === 'inline' ? 'active' : ''}`}
              onClick={() => setViewMode('inline')}
              title={t('diff.inline')}
            >
              <AlignLeft size={18} />
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'side-by-side' ? (
        <div className="diff-side-by-side">
          <div className="diff-panel original">
            <div className="panel-header">{t('diff.original')}</div>
            <div className="panel-content">
              {lines.map((line) => {
                const lowConf = isLowConfidence(lineConfidence[String(line.originalIndex)]);
                return (
                  <div key={line.originalIndex} className={`diff-line ${lowConf ? 'low-confidence' : ''}`}>
                    <span className="line-number">{line.originalIndex + 1}</span>
                    <AnnotatedLine
                      line={line.original}
                      annotations={annotations}
                      side="source"
                      marks={marksByLine[line.originalIndex] || []}
                      onAnnotationHover={handleAnnotationHover}
                      onAnnotationLeave={handleAnnotationLeave}
                      onAnnotationClick={handleAnnotationClick}
                      onMarkShow={handleMarkShow}
                      onMarkLeave={handleMarkLeave}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="diff-panel translated">
            <div className="panel-header">{t('diff.translation')}</div>
            <div className="panel-content">
              {lines.map((line) => {
                const conf = lineConfidence[String(line.originalIndex)];
                const lowConf = isLowConfidence(conf);
                return (
                  <div key={line.originalIndex} className={`diff-line ${lowConf ? 'low-confidence' : ''}`}>
                    <span className="line-number">{line.originalIndex + 1}</span>
                    <ConfidenceBadge confidence={conf} />
                    <AnnotatedLine
                      line={line.translated}
                      annotations={annotations}
                      side="target"
                      onAnnotationHover={handleAnnotationHover}
                      onAnnotationLeave={handleAnnotationLeave}
                      onAnnotationClick={handleAnnotationClick}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          {deeplOpen && (
            <div className="diff-panel deepl">
              <div className="panel-header">
                <Sparkles size={13} />
                <span>{t('diff.deepl', 'DeepL')}</span>
              </div>
              <div className="panel-content">
                {deeplStatus === 'loading' && (
                  <div className="deepl-state-message">
                    <Loader2 size={16} className="diff-deepl-spinner" />
                    <span>{t('diff.deeplLoading', 'Fetching DeepL translation...')}</span>
                  </div>
                )}
                {deeplStatus === 'unavailable' && (
                  <div className="deepl-state-message muted">
                    {deeplReason === 'key_missing' && t('diff.deeplKeyMissing', 'DeepL comparison is not configured yet')}
                    {deeplReason === 'unsupported_language' && t('diff.deeplUnsupportedLang', "DeepL doesn't support this language pair")}
                    {deeplReason === 'api_error' && t('diff.deeplApiError', 'DeepL temporarily unavailable, try again later')}
                  </div>
                )}
                {deeplStatus === 'available' && lines.map((line, index) => (
                  <div
                    key={line.originalIndex}
                    className="diff-line deepl-line"
                    style={{ animationDelay: `${Math.min(index, 30) * 30}ms` }}
                  >
                    <span className="line-number">{line.originalIndex + 1}</span>
                    <span className="line-content">{line.deepl}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="diff-inline">
          {lines.map((line) => {
            const conf = lineConfidence[String(line.originalIndex)];
            const lowConf = isLowConfidence(conf);
            return (
              <div key={line.originalIndex} className={`diff-inline-pair ${lowConf ? 'low-confidence' : ''}`}>
                <div className="diff-line original">
                  <span className="line-number">{line.originalIndex + 1}</span>
                  <span className="line-label">{t('diff.original')}:</span>
                  <AnnotatedLine
                    line={line.original}
                    annotations={annotations}
                    side="source"
                    marks={marksByLine[line.originalIndex] || []}
                    onAnnotationHover={handleAnnotationHover}
                    onAnnotationLeave={handleAnnotationLeave}
                    onAnnotationClick={handleAnnotationClick}
                    onMarkShow={handleMarkShow}
                    onMarkLeave={handleMarkLeave}
                  />
                </div>
                <div className="diff-line translated">
                  <span className="line-number"></span>
                  <ConfidenceBadge confidence={conf} />
                  <span className="line-label">{t('diff.translation')}:</span>
                  <AnnotatedLine
                    line={line.translated}
                    annotations={annotations}
                    side="target"
                    onAnnotationHover={handleAnnotationHover}
                    onAnnotationLeave={handleAnnotationLeave}
                    onAnnotationClick={handleAnnotationClick}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeMark && documentId && (
        <HandwritingPlace
          documentId={documentId}
          value={activeMark.mark}
          position={activeMark.position}
          onMouseEnter={handleMarkCardEnter}
          onMouseLeave={handleMarkLeave}
        />
      )}

      {activeAnnotation && (
        <AnnotationTooltip
          annotation={activeAnnotation}
          position={tooltipPosition}
          sourceLang={sourceLang}
          targetLang={targetLang}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        />
      )}
    </div>
  );
}

export default DiffViewer;
